import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Wallet, Building2, CheckCircle2, XCircle, Clock, Eye, ExternalLink,
  ArrowLeft, FileText,
} from 'lucide-react';
import { contractorLabsAPI } from '../../services/api';
import { formatDateShort } from '../../utils/date';

const SAR = (n) => `${Number(n || 0).toFixed(2)} ر.س`;
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

const StatusBadge = ({ status }) => {
  const map = {
    pending:   'bg-amber-50 text-amber-700 border-amber-200',
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected:  'bg-red-50 text-red-700 border-red-200',
  };
  const label = { pending: 'قيد المراجعة', confirmed: 'مؤكد', rejected: 'مرفوض' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${map[status] || map.pending}`}>
      {label[status] || status}
    </span>
  );
};

const ContractorAccounts = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLab, setSelectedLab] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => { fetchSummary(); }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await contractorLabsAPI.getAccountsSummary();
      setSummary(data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'تعذّر تحميل البيانات');
    }
    setLoading(false);
  };

  const openDetail = async (lab) => {
    setSelectedLab(lab);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const { data } = await contractorLabsAPI.getLabStatement(lab._id);
      setDetail(data?.data);
    } catch (e) {
      toast.error('تعذّر تحميل كشف الحساب');
    }
    setLoadingDetail(false);
  };

  const handleConfirm = async (paymentId) => {
    try {
      await contractorLabsAPI.confirmPayment(paymentId, { admin_notes: 'تم تأكيد الاستلام' });
      toast.success('تم تأكيد استلام المبلغ');
      openDetail(selectedLab); fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل التأكيد');
    }
  };

  const handleReject = async (paymentId) => {
    if (!rejectNote.trim()) { toast.error('سبب الرفض مطلوب'); return; }
    try {
      await contractorLabsAPI.rejectPayment(paymentId, { admin_notes: rejectNote });
      toast.success('تم رفض الإشعار');
      setRejectingId(null); setRejectNote('');
      openDetail(selectedLab); fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل الرفض');
    }
  };

  const totalOutstanding = summary.reduce((s, x) => s + (x.outstanding || 0), 0);
  const totalPending = summary.reduce((s, x) => s + (x.pending_count || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl" style={{ fontFamily: "'Readex Pro', sans-serif" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">حسابات المختبرات المتعاقدة</h1>
            <p className="text-slate-500 text-sm">المستحقات وإشعارات التحويل</p>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-slate-500 text-sm mb-1">إجمالي المستحقات</p>
            <p className="text-2xl font-bold text-amber-600">{SAR(totalOutstanding)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-slate-500 text-sm mb-1">إشعارات قيد المراجعة</p>
            <p className="text-2xl font-bold text-blue-600">{totalPending}</p>
          </div>
        </div>

        {/* Summary table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-right">المختبر</th>
                  <th className="px-4 py-3 text-right">إجمالي الحجوزات</th>
                  <th className="px-4 py-3 text-right">المدفوع</th>
                  <th className="px-4 py-3 text-right">المستحق</th>
                  <th className="px-4 py-3 text-right">إشعارات قيد المراجعة</th>
                  <th className="px-4 py-3 text-right">آخر نشاط</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">جاري التحميل...</td></tr>
                ) : summary.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">لا يوجد مختبرات متعاقدة</td></tr>
                ) : summary.map((lab) => (
                  <tr key={lab._id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-800">{lab.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{SAR(lab.billed)}</td>
                    <td className="px-4 py-3 text-emerald-700 font-medium">{SAR(lab.paid)}</td>
                    <td className={`px-4 py-3 font-bold ${lab.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {SAR(lab.outstanding)}
                    </td>
                    <td className="px-4 py-3">
                      {lab.pending_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs border border-amber-200">
                          <Clock className="w-3 h-3" /> {lab.pending_count} ({SAR(lab.pending_amount)})
                        </span>
                      ) : <span className="text-slate-400 text-sm">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-sm">
                      {lab.last_booking_date ? formatDateShort(lab.last_booking_date) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => openDetail(lab)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                        <Eye className="w-4 h-4" /> تفاصيل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {selectedLab && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLab(null)}>
          <div className="bg-white rounded-2xl w-[95vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{selectedLab.name}</h2>
                <p className="text-sm text-slate-500">كشف حساب + إشعارات التحويل</p>
              </div>
              <button onClick={() => setSelectedLab(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <XCircle className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {loadingDetail || !detail ? (
                <p className="text-center text-slate-400 py-8">جاري التحميل...</p>
              ) : (
                <>
                  {/* Totals */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500">مطلوب</p>
                      <p className="text-lg font-bold text-slate-800">{SAR(detail.all_time.billed)}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500">مدفوع</p>
                      <p className="text-lg font-bold text-emerald-700">{SAR(detail.all_time.paid)}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${detail.all_time.outstanding > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                      <p className="text-xs text-slate-500">المستحق</p>
                      <p className={`text-lg font-bold ${detail.all_time.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {SAR(detail.all_time.outstanding)}
                      </p>
                    </div>
                  </div>

                  {/* Payments */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 mb-2">إشعارات التحويل</h3>
                    <div className="border border-gray-200 rounded-xl overflow-x-auto">
                      <table className="w-full min-w-[700px] text-sm">
                        <thead className="bg-gray-50 text-xs text-slate-500">
                          <tr>
                            <th className="px-3 py-2 text-right">المبلغ</th>
                            <th className="px-3 py-2 text-right">تاريخ التحويل</th>
                            <th className="px-3 py-2 text-right">رقم الحوالة</th>
                            <th className="px-3 py-2 text-right">الإيصال</th>
                            <th className="px-3 py-2 text-right">الحالة</th>
                            <th className="px-3 py-2 text-right">ملاحظات</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.payments.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-6 text-slate-400">لا توجد إشعارات</td></tr>
                          ) : detail.payments.map((p) => (
                            <React.Fragment key={p._id}>
                              <tr className="border-t border-gray-100">
                                <td className="px-3 py-2 font-bold">{SAR(p.amount)}</td>
                                <td className="px-3 py-2 text-slate-600">{formatDateShort(p.transfer_date)}</td>
                                <td className="px-3 py-2 font-mono text-xs">{p.reference_number || '—'}</td>
                                <td className="px-3 py-2">
                                  {p.receipt_url ? (
                                    <a href={`${API_BASE}${p.receipt_url}`} target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs">
                                      <ExternalLink className="w-3 h-3" /> عرض
                                    </a>
                                  ) : <span className="text-slate-400 text-xs">—</span>}
                                </td>
                                <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                                <td className="px-3 py-2 text-slate-600 text-xs">{p.admin_notes || '—'}</td>
                                <td className="px-3 py-2 text-left">
                                  {p.status === 'pending' && (
                                    <div className="flex gap-1 justify-end">
                                      <button onClick={() => handleConfirm(p._id)}
                                        className="px-2 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" /> تأكيد
                                      </button>
                                      <button onClick={() => setRejectingId(p._id)}
                                        className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 flex items-center gap-1">
                                        <XCircle className="w-3 h-3" /> رفض
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                              {rejectingId === p._id && (
                                <tr className="bg-red-50/50">
                                  <td colSpan={7} className="px-3 py-3">
                                    <div className="flex gap-2">
                                      <input type="text" placeholder="سبب الرفض"
                                        value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-red-200 rounded-lg text-sm" />
                                      <button onClick={() => handleReject(p._id)}
                                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                                        تأكيد الرفض
                                      </button>
                                      <button onClick={() => { setRejectingId(null); setRejectNote(''); }}
                                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                                        إلغاء
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Bookings */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 mb-2">الحجوزات</h3>
                    <div className="border border-gray-200 rounded-xl overflow-x-auto">
                      <table className="w-full min-w-[500px] text-sm">
                        <thead className="bg-gray-50 text-xs text-slate-500">
                          <tr>
                            <th className="px-3 py-2 text-right">رقم الحجز</th>
                            <th className="px-3 py-2 text-right">التاريخ</th>
                            <th className="px-3 py-2 text-right">المريض</th>
                            <th className="px-3 py-2 text-right">المبلغ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.bookings.length === 0 ? (
                            <tr><td colSpan={4} className="text-center py-6 text-slate-400">لا توجد حجوزات</td></tr>
                          ) : detail.bookings.map((b) => (
                            <tr key={b._id} className="border-t border-gray-100">
                              <td className="px-3 py-2 font-mono font-bold">{b.booking_no}</td>
                              <td className="px-3 py-2 text-slate-600">{formatDateShort(b.created_at)}</td>
                              <td className="px-3 py-2 text-slate-700">{b.patient_name}</td>
                              <td className="px-3 py-2 font-medium">{SAR(b.total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorAccounts;
